import React, { CSSProperties, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Button, ButtonGroup } from '@material-ui/core';
import { FontIcon } from '@moderepo/mode-app-components';
import { useSelectProject } from '../../utils/dataStateHooks';
import { PageInfoItemDevice, PageInfoItemHome, PageInfoItemUser, usePageInfoStyles } from '../misc/PageInfoUtils';
import { useSelectRobot } from './rcDataStateHooks';
import { RCRouteHelper } from './RCRouteHelper';
import { useGetURLParamEntityIdAsNumber, useGetURLParamEntityIdAsString } from '../../utils/utils';


interface PageInfoItemRobotProps {
    readonly projectId: number;
    readonly userId?: number | undefined;
    readonly homeId: number | undefined;
    readonly robotId: string | undefined;
}



/**
 * A component that display Robot info
 */
const PageInfoItemRobot: React.FC<PageInfoItemRobotProps> = (props: PageInfoItemRobotProps) => {
    const navigate = useNavigate();
    const robot = useSelectRobot(props.projectId, props.homeId, props.robotId);

    const classes = usePageInfoStyles();
    const routeHelper = RCRouteHelper.getInstance(navigate);

    const onItemListingClick = useCallback(() => {
        routeHelper.gotoRobotsPage({
            projectId: props.projectId,
        });
    }, [props.projectId, routeHelper]);

    const onItemInstanceClick = useCallback(() => {
        if (!robot) {
            return;
        }
        routeHelper.gotoRobotPage({
            projectId: props.projectId,
            userId   : props.userId,
            homeId   : robot.homeId,
            deviceId : robot.deviceId,
            robotId  : robot.id,
        });
    }, [props.projectId, props.userId, robot, routeHelper]);

    // There is a chance there is no robot object when none of the props is provided therefore we need to return empty element
    if (!robot) {
        return (
            <></>
        );
    }

    return (
        <ButtonGroup
            variant="text"
            className={clsx(classes.infoItem)}
        >
            <Button className={classes.infoItemIconButton} onClick={onItemListingClick}>
                <FontIcon className={classes.infoItemIcon} iconName="mode-icon rc-logo" />
            </Button>
            <Button
                onClick={onItemInstanceClick}
            >
                <div className={classes.infoItemLabel}>
                    {robot.name && (
                        <div className={classes.infoItemName}>
                            {robot.name}
                        </div>
                    )}
                    <div className={classes.infoItemId}>
                        #{robot.id}
                    </div>
                </div>
            </Button>
        </ButtonGroup>
    );
};



export interface RCPageInfoBarContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
}


export const RCPageInfoBarContainer: React.FC<RCPageInfoBarContainerProps> = (props: RCPageInfoBarContainerProps) => {

    const projectId = useGetURLParamEntityIdAsNumber('projectId');
    const userId = useGetURLParamEntityIdAsNumber('userId');
    const homeId = useGetURLParamEntityIdAsNumber('homeId');
    const deviceId = useGetURLParamEntityIdAsNumber('deviceId');
    const robotId = useGetURLParamEntityIdAsString('robotId');

    // If deviceId is not provided, see if we can get the deviceId from the robot if robotId is provided.
    const robot = useSelectRobot(projectId, homeId, robotId);
    const resolvedDeviceId = !deviceId && robot && robot.deviceId ? robot.deviceId : deviceId;

    const project = useSelectProject(projectId);
    const classes = usePageInfoStyles();


    if (project) {
        return (
            <div className={clsx(classes.root, props.className)} style={props.style}>
                <PageInfoItemUser projectId={project.id} userId={userId} homeId={homeId} deviceId={resolvedDeviceId} />
                <PageInfoItemHome projectId={project.id} userId={userId} homeId={homeId} deviceId={resolvedDeviceId} />
                <PageInfoItemDevice projectId={project.id} userId={userId} homeId={homeId} deviceId={resolvedDeviceId} icon="router" />
                <PageInfoItemRobot projectId={project.id} userId={userId} homeId={homeId} robotId={robotId} />
            </div>
        );
    }

    return (
        <></>
    );
};
